import { formatRoute } from "@/utils/stringHelper";
import { ApiEnum } from "./api-enum";
import { BaseRequest } from "./core/base-request";
import type { Article, BaseResponse, DeleteArticleOptions, PublishArticleOptions, UpdateArticleOptions } from "./types";

export class ArticleService extends BaseRequest {
  public static async publishArticle(options: PublishArticleOptions): Promise<BaseResponse<null>> {
    return ArticleService._post<null>(
      ApiEnum.PublishArticle,
      {
        attributes: options.attributes,
        site: options.site,
        ...(options.isDraft ? { isDraft: true } : {}),
        ...(options.createdAt != null ? { createdAt: options.createdAt } : {}),
        ...(options.notBefore !== undefined ? { notBefore: options.notBefore } : {}),
      },
      { requiredAuth: true, mapperKey: ApiEnum.PublishArticle },
    );
  }

  public static async updateArticle(options: UpdateArticleOptions): Promise<BaseResponse<null>> {
    return ArticleService._post<null>(
      ApiEnum.UpdateArticle,
      {
        attributes: options.article.attributes,
        articleId: options.articleId,
        ...(typeof options.article.isDraft === "boolean" ? { isDraft: options.article.isDraft } : {}),
        ...(options.article.createdAt != null ? { createdAt: options.article.createdAt } : {}),
        ...(options.article.notBefore !== undefined ? { notBefore: options.article.notBefore } : {}),
      },
      { requiredAuth: true, mapperKey: ApiEnum.UpdateArticle },
    );
  }

  public static async deleteArticle(options: DeleteArticleOptions): Promise<BaseResponse<null>> {
    return ArticleService._post<null>(
      ApiEnum.DeleteArticle,
      { articleId: options.articleId },
      { requiredAuth: true, mapperKey: ApiEnum.DeleteArticle },
    );
  }

  public static async getArticle(options: {
    articleId: string;
  }): Promise<BaseResponse<Article>> {
    const url = formatRoute(ApiEnum.GetArticle, { id: options.articleId });
    return ArticleService._get<Article>(url, {}, { requiredAuth: true, mapperKey: ApiEnum.GetArticle });
  }

  public static async getArticles(options: {
    publisher: string;
    isDraft?: boolean;
    offset?: number;
    limit?: number;
  }): Promise<BaseResponse<Article[]>> {
    const url = formatRoute(ApiEnum.PublisherDetailArticles, {
      id: options.publisher,
    });
    const params: Record<string, any> = {};
    if (options.isDraft) params.isDraft = true;
    if (typeof options.offset === "number") params.offset = options.offset;
    if (typeof options.limit === "number") params.limit = options.limit;
    if (params.offset === undefined && params.limit === undefined && !options.isDraft) {
      params.limit = 1000;
    }
    return ArticleService._get<Article[]>(url, params, {
      requiredAuth: true,
      mapperKey: ApiEnum.PublisherDetailArticles,
    });
  }
}
